import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApiUrl } from "../../../../api";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PostItem {
    content_code: string;
    content_index: string;
    title: string;
    no: number;
    selected: boolean;
    visible: boolean;
    category_code: string;
    is_visible: number;
}

interface ConfirmModal {
    open: boolean;
    title: string;
    description: string;
    onConfirm: (() => Promise<void>) | null;
}

interface CategoryItem {
    category_code: string;
    name: string;
}

function SortableRow({
    item,
    showOrder,
    onRowClick,
    onEdit,
    onDelete,
    onToggleVisible,
    onSelectItem,
    onCopy,
}: {
    item: PostItem;
    showOrder: boolean;
    onRowClick: (item: PostItem) => void;
    onEdit: (item: PostItem) => void;
    onDelete: (item: PostItem) => void;
    onToggleVisible: (id: string) => void;
    onSelectItem: (id: string) => void;
    onCopy: (item: PostItem) => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.content_code });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? "#fdf8f0" : undefined,
        zIndex: isDragging ? 9999 : undefined,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
            onClick={() => onRowClick(item)}
        >
            <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={item.selected} onChange={() => onSelectItem(item.content_index)} className="w-4 h-4 cursor-pointer accent-[#887267]" />
            </td>
            <td className="px-5 py-4 text-[13px] text-[#5a3d32]">{item.no}</td>
            <td className="px-5 py-4 text-[13px] font-medium text-[#2a1f1a]">{item.title}</td>
            <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={() => onToggleVisible(item.content_index)}
                    className={`relative w-16 h-7 rounded-3xl transition-colors ${item.visible ? "bg-[#887267]" : "bg-[#d0c4be]"}`}
                >
                    <div className={`absolute top-0.5 w-10 h-6 bg-white rounded-3xl transition-all ${item.visible ? "right-0.5" : "left-0.5"}`} />
                    <span className={`absolute top-1/2 -translate-y-1/2 text-[11px] font-normal ${item.visible ? "right-2 text-white" : "left-2 text-[#888]"}`}>
                        {item.visible ? "보임" : "숨김"}
                    </span>
                </button>
            </td>
            {showOrder && (
                <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-0.5 cursor-grab active:cursor-grabbing p-2 items-center" {...attributes} {...listeners}>
                        <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                        <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                        <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                    </div>
                </td>
            )}
            <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-1.5 justify-center">
                    <button onClick={() => onCopy(item)} className="px-3 py-1 text-[12px] border border-green-300 text-green-600 rounded hover:bg-green-50 transition-colors whitespace-nowrap">복사</button>
                    <button onClick={() => onEdit(item)} className="px-3 py-1 text-[12px] border border-[#887267] text-[#887267] rounded hover:bg-[#f5f2f0] transition-colors whitespace-nowrap">수정</button>
                    <button onClick={() => onDelete(item)} className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded hover:bg-red-50 transition-colors whitespace-nowrap">삭제</button>
                </div>
            </td>
        </tr>
    );
}

export default function PostList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const kioskCode = searchParams.get("kiosk_code") || localStorage.getItem("kiosk_code") || "";
    const preSelectedCategoryCode = searchParams.get("category_code");

    const [postData, setPostData] = useState<PostItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectAll, setSelectAll] = useState(false);
    const [filterType, setFilterType] = useState("all");
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [categoryList, setCategoryList] = useState<CategoryItem[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<CategoryItem | null>(null);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState("");
    const [searchKeyword, setSearchKeyword] = useState("");
    const [hasFetched, setHasFetched] = useState(false);
    const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, title: "", description: "", onConfirm: null });
    const [resultModal, setResultModal] = useState({ open: false, title: "", description: "" });

    const filterDropdownRef = useRef<HTMLDivElement>(null);
    const categoryDropdownRef = useRef<HTMLDivElement>(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const showOrder = hasFetched && filterType !== "all";
    const getCmsToken = () => localStorage.getItem("cms_access_token");

    const fetchContents = async (categoryCode: string | null = null) => {
        if (!kioskCode) return;
        setLoading(true);
        try {
            const token = getCmsToken();
            const headers = { Authorization: `Bearer ${token}` };

            if (categoryCode || (filterType === "category" && selectedCategory)) {
                const code = categoryCode || selectedCategory!.category_code;
                const res = await fetch(getApiUrl(`/api/categories/${encodeURIComponent(code)}/all-items`), { headers });
                if (res.ok) {
                    const data = await res.json();
                    const rawList = Array.isArray(data?.items) ? data.items : [];
                    const sorted = [...rawList].sort((a, b) => a.sort_order - b.sort_order);
                    setPostData(sorted.map((item, idx) => ({
                        ...item.data,
                        no: idx + 1,
                        selected: false,
                        visible: Number(item.data.is_visible) === 1,
                        content_index: item.data.content_code,
                    })));
                    setHasFetched(true);
                }
                return;
            }

            let query = "";
            if (filterType === "content" && searchKeyword) query = `&title=${encodeURIComponent(searchKeyword)}`;
            const res = await fetch(getApiUrl(`/api/contents/by-kiosk/${kioskCode}?skip=0&limit=50${query}`), { headers });
            if (res.ok) {
                const data = await res.json();
                const list = Array.isArray(data?.items) ? data.items : [];
                setPostData(list.map((item: PostItem & { is_visible: number }, idx: number) => ({
                    ...item,
                    no: idx + 1,
                    selected: false,
                    visible: Number(item.is_visible) === 1,
                    content_index: item.content_code,
                })));
                setHasFetched(true);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const fetchCategories = async () => {
        if (!kioskCode) return;
        try {
            const res = await fetch(getApiUrl(`/api/solutions/by-kiosk/${kioskCode}/categories`), {
                headers: { Authorization: `Bearer ${getCmsToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCategoryList(data);
                if (preSelectedCategoryCode) {
                    const matched = data.find((c: CategoryItem) => c.category_code === preSelectedCategoryCode);
                    if (matched) { setFilterType("category"); setSelectedCategory(matched); }
                }
            }
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        fetchCategories();
        fetchContents(preSelectedCategoryCode || null);
        const clickOutside = (e: MouseEvent) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) setFilterDropdownOpen(false);
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) setCategoryDropdownOpen(false);
        };
        document.addEventListener("mousedown", clickOutside);
        return () => document.removeEventListener("mousedown", clickOutside);
    }, [kioskCode, preSelectedCategoryCode]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = postData.findIndex(i => i.content_code === active.id);
        const newIndex = postData.findIndex(i => i.content_code === over.id);
        const newData = arrayMove(postData, oldIndex, newIndex).map((item, i) => ({ ...item, no: i + 1 }));
        setPostData(newData);
        try {
            const categoryCode = selectedCategory?.category_code;
            if (!categoryCode) return;
            await fetch(getApiUrl(`/api/categories/${categoryCode}/contents/sort`), {
                method: "PUT",
                headers: { Authorization: `Bearer ${getCmsToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({ items: newData.map((item, i) => ({ content_code: item.content_code, sort_order: i + 1 })) })
            });
        } catch (e) { console.error(e); }
    };

    const handleToggleVisible = async (id: string) => {
        const target = postData.find(item => item.content_index === id);
        if (!target) return;
        const newState = target.visible ? 0 : 1;
        try {
            const res = await fetch(getApiUrl(`/api/contents/${target.content_code}/visibility`), {
                method: "PATCH",
                headers: { Authorization: `Bearer ${getCmsToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({ is_visible: newState })
            });
            if (res.ok) setPostData(prev => prev.map(item => item.content_index === id ? { ...item, visible: !item.visible } : item));
        } catch (e) { console.error(e); }
    };

    const handleCopy = async (item: PostItem) => {
        try {
            const res = await fetch(getApiUrl(`/api/contents/${item.content_code}/duplicate`), {
                method: "POST",
                headers: { Authorization: `Bearer ${getCmsToken()}` }
            });
            if (res.ok) { fetchContents(); setResultModal({ open: true, title: "복사되었습니다.", description: "" }); }
        } catch (e) { console.error(e); }
    };

    const handleDelete = (item: PostItem) => {
        setConfirmModal({
            open: true,
            title: "이 컨텐츠를 삭제하시겠습니까?",
            description: "삭제된 데이터는 복구할 수 없습니다.",
            onConfirm: async () => {
                try {
                    await fetch(getApiUrl(`/api/contents/${item.content_code}`), {
                        method: "DELETE",
                        headers: { Authorization: `Bearer ${getCmsToken()}` }
                    });
                    fetchContents();
                } catch (e) { console.error(e); }
                finally { setConfirmModal(prev => ({ ...prev, open: false })); }
            }
        });
    };

    const handleDeleteSelected = () => {
        const selected = postData.filter(item => item.selected);
        if (selected.length === 0) return;
        setConfirmModal({
            open: true,
            title: `선택한 ${selected.length}개의 컨텐츠를 삭제하시겠습니까?`,
            description: "삭제된 데이터는 복구할 수 없습니다.",
            onConfirm: async () => {
                try {
                    await Promise.all(selected.map(item =>
                        fetch(getApiUrl(`/api/contents/${item.content_code}`), { method: "DELETE", headers: { Authorization: `Bearer ${getCmsToken()}` } })
                    ));
                    setSelectAll(false);
                    fetchContents();
                } catch (e) { console.error(e); }
                finally { setConfirmModal(prev => ({ ...prev, open: false })); }
            }
        });
    };

    const handleSelectAll = () => {
        const next = !selectAll;
        setSelectAll(next);
        setPostData(postData.map(item => ({ ...item, selected: next })));
    };

    const handleSelectItem = (id: string) => {
        setPostData(postData.map(item => item.content_index === id ? { ...item, selected: !item.selected } : item));
    };

    const filteredCategories = categoryList.filter(cat => cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase()));
    const getFilterLabel = (type = filterType) => ({ all: "전체보기", category: "상위 메뉴명", content: "컨텐츠명" })[type] || "";
    const hasSelected = postData.some(item => item.selected);
    const selectedCount = postData.filter(i => i.selected).length;

    return (
        <div className="bg-[#f5f2f0] min-h-screen p-6">
            <div className="max-w-[1200px] mx-auto">

                {/* 헤더 */}
                <div className="flex items-start justify-between mb-6">
                    <h1 className="text-[22px] font-extrabold text-[#2a1f1a]">컨텐츠 관리</h1>
                    <div className="flex gap-2">
                        {hasSelected && (
                            <button onClick={handleDeleteSelected} className="px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 transition-colors text-[13px] font-semibold">
                                선택 삭제 ({selectedCount})
                            </button>
                        )}
                        <button
                            onClick={() => navigate(`/admin/kiosk/content/register?kiosk_code=${kioskCode}`)}
                            className="px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] transition-colors text-[13px] font-semibold flex items-center gap-1.5"
                        >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="white"><path d="M14 8H8V14H6V8H0V6H6V0H8V6H14V8Z" /></svg>
                            신규 컨텐츠 등록
                        </button>
                    </div>
                </div>

                {/* 필터 */}
                <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative" ref={filterDropdownRef}>
                            <button
                                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                                className="w-36 h-9 px-3 border border-[#e8e0dc] rounded-lg bg-white text-[13px] flex items-center justify-between text-[#2a1f1a]"
                            >
                                {getFilterLabel()}
                                <svg className={`w-3 h-3 transition-transform ${filterDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" strokeWidth={2} /></svg>
                            </button>
                            {filterDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-[#e8e0dc] rounded-lg shadow-lg z-30">
                                    {["all", "category", "content"].map(type => (
                                        <button key={type} onClick={() => { setFilterType(type); setFilterDropdownOpen(false); setSelectedCategory(null); setSearchKeyword(""); setHasFetched(false); }}
                                            className="w-full px-3 py-2 text-left hover:bg-[#f5f2f0] text-[13px] text-[#2a1f1a]">
                                            {getFilterLabel(type)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {filterType === "category" && (
                            <div className="relative" ref={categoryDropdownRef}>
                                <button onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                                    className="w-56 h-9 px-3 border border-[#e8e0dc] rounded-lg bg-white text-[13px] flex items-center justify-between text-[#2a1f1a]">
                                    <span className="truncate">{selectedCategory ? selectedCategory.name : "메뉴를 선택하세요"}</span>
                                    <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" strokeWidth={2} /></svg>
                                </button>
                                {categoryDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-[#e8e0dc] rounded-lg shadow-lg z-30">
                                        <div className="p-2 border-b border-[#f5f2f0]">
                                            <input type="text" placeholder="검색..." value={categorySearchTerm}
                                                onChange={(e) => setCategorySearchTerm(e.target.value)}
                                                className="w-full px-2 py-1 border border-[#e8e0dc] rounded text-[12px] outline-none focus:border-[#887267]" />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredCategories.map(cat => (
                                                <button key={cat.category_code} onClick={() => { setSelectedCategory(cat); setCategoryDropdownOpen(false); setCategorySearchTerm(""); }}
                                                    className="w-full px-3 py-2 text-left hover:bg-[#f5f2f0] text-[13px] text-[#2a1f1a]">
                                                    {cat.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {filterType === "content" && (
                            <input type="text" value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && fetchContents()}
                                placeholder="검색어를 입력하세요"
                                className="w-64 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                        )}

                        <button onClick={() => fetchContents(null)}
                            className="h-9 px-5 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors">
                            조회
                        </button>
                    </div>
                </div>

                {/* 테이블 */}
                <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                                <th className="px-5 py-3 w-[48px]">
                                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="w-4 h-4 cursor-pointer accent-[#887267]" />
                                </th>
                                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">NO</th>
                                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium">컨텐츠명</th>
                                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">노출</th>
                                {showOrder && <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">순서</th>}
                                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[160px]">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={showOrder ? 6 : 5} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
                            ) : postData.length === 0 ? (
                                <tr><td colSpan={showOrder ? 6 : 5} className="text-center py-12 text-gray-300 text-[13px]">등록된 컨텐츠가 없습니다.</td></tr>
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={postData.map(i => i.content_code)} strategy={verticalListSortingStrategy}>
                                        {postData.map(item => (
                                            <SortableRow
                                                key={item.content_code}
                                                item={item}
                                                showOrder={showOrder}
                                                onRowClick={(item) => navigate(`/admin/kiosk/content/detail/${item.content_code}?kiosk_code=${kioskCode}&category_code=${item.category_code}`)}
                                                onEdit={(item) => navigate(`/admin/kiosk/content/edit/${item.content_code}?kiosk_code=${kioskCode}&category_code=${item.category_code}`)}
                                                onDelete={handleDelete}
                                                onToggleVisible={handleToggleVisible}
                                                onSelectItem={handleSelectItem}
                                                onCopy={handleCopy}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 삭제 확인 모달 */}
            {confirmModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">{confirmModal.title}</h3>
                        {confirmModal.description && <p className="text-[13px] text-gray-400 mb-5">{confirmModal.description}</p>}
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={() => confirmModal.onConfirm?.()}
                                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold">삭제</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 결과 모달 */}
            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{resultModal.title}</p>
                        <button onClick={() => setResultModal(prev => ({ ...prev, open: false }))}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
                    </div>
                </div>
            )}
        </div>
    );
}